import { Component, OnInit, ViewChild } from '@angular/core';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, MonitorStatus, HelpIndex } from '../../../models/enums';
import { GenericColDef, FilterTemplateType, GridRowSelectionType, CellTemplateType, AlignDirection, GridContext } from '../../../models/grid-context';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { MonitoringNode } from '../../../models/monitoring-node';
import { AEComponentNode } from '../../../models/ae-component-node';
import { StorageNode } from '../../../models/storage-node';
import { CustomHeaderCheckboxComponent } from '../../grid-templates/custom-header-checkbox/custom-header-checkbox.component';
import { HttpService } from '../../../services/http/http.service';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { ConnectorStatusList } from '../../../models/connector-status-list';
import { DataAreaStatusList } from '../../../models/data-area-status-list';
import * as _ from 'lodash';

@Component({
    standalone: false,
    selector: 'res-monitoring',
    templateUrl: './monitoring.component.html',
    styleUrls: ['./monitoring.component.scss']
})

export class MonitoringComponent implements OnInit {
    @ViewChild('realmNodesGrid', { static: true }) realmNodesGrid: GenericGridComponent;
    @ViewChild('aeComponentsGrid', { static: true }) aeComponentsGrid: GenericGridComponent;
    @ViewChild('storageGrid', { static: true }) storageGrid: GenericGridComponent;
    @ViewChild('connectorsGrid', { static: true }) connectorsGrid: GenericGridComponent;
    @ViewChild('dataAreasGrid', { static: true }) dataAreasGrid: GenericGridComponent;
    customHeaderCheckboxComponent: CustomHeaderCheckboxComponent;
    gridsUpdated = false;
    // show AE Components and Storage grids based on Realm Nodes Grid row selection 
    showAEComponentsandStorageGrids: boolean = false;
    realmNodesSelectedRow: any = '';
    selectedRealmNodeName: string = '';
    nodeNames: Array<string> = [];
    rightBottomPanelText: string = '';

    // data area list info
    initialDataAreaStatusList = new Array<DataAreaStatusList>();

    // Connectors list info
    initialConnectorStatusList = new Array<ConnectorStatusList>();
    connectorNameColumnFilterValue = '';
    statusColumnFilterValue = '';
    orgNameColumnFilterValue = '';

    selectedConnectorGridName: string;
    connectorsGridSelectedRow: any;

    // Realm Nodes Grid Info
    realmNodesGridName = 'Realm Nodes Grid';
    realmNodegridAutoID = 'realm-nodes';
    isrealmNodesGridBusy = false;
    realmNodesGridBusyMessage = '';
    realmNodesGridRowSelection = GridRowSelectionType.single;
    realmNodesColDefs = new Array<GenericColDef>(
        {
            headerName: 'Node Name',
            field: 'name',
            automationId: this.realmNodegridAutoID,
            filterType: FilterTemplateType.freeform,
            minWidth: 30,
            defaultSortAscending: true,
            defaultSortBy: 'name',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Node Type',
            field: 'analyticNodeMode',
            automationId: this.realmNodegridAutoID,
            minWidth: 30
        },
        {
            headerName: 'AE Components',
            field: 'aeComponentMonitorStatus',
            automationId: this.realmNodegridAutoID,
            headerAlignDirection: AlignDirection.center,
            cellAlignDirection: AlignDirection.center,
            width: 115,
            minWidth: 115,
            maxWidth: 115,
            suppressSizeToFit: true,
            cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
            alternativeFieldNames: { iconClass: 'statusIconClass', iconColor: 'statusIconColor' }
        },
        {
            headerName: 'Storage',
            field: 'storageMonitorStatus',
            automationId: this.realmNodegridAutoID,
            headerAlignDirection: AlignDirection.center,
            cellAlignDirection: AlignDirection.center,
            width: 70,
            minWidth: 70,
            maxWidth: 70,
            cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
            alternativeFieldNames: { iconClass: 'statusIconClass', iconColor: 'statusIconColor' }
        },
        {
            headerName: 'Connectors',
            field: 'connectorMonitorStatus',
            automationId: this.realmNodegridAutoID,
            cellAlignDirection: AlignDirection.center,
            headerAlignDirection: AlignDirection.center,
            cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
            alternativeFieldNames: { iconClass: 'statusIconClass', iconColor: 'statusIconColor' },
            width: 90,
            minWidth: 90,
            maxWidth: 90
        },
        {
            headerName: 'Cores',
            field: 'numberCores',
            automationId: this.realmNodegridAutoID,
            cellAlignDirection: AlignDirection.right,
            headerAlignDirection: AlignDirection.right,
            width: 60,
            minWidth: 60,
            maxWidth: 60
        },
        {
            headerName: 'Threads',
            field: 'numberThreadsExecuting',
            automationId: this.realmNodegridAutoID,
            cellAlignDirection: AlignDirection.right,
            headerAlignDirection: AlignDirection.right,
            width: 70,
            minWidth: 70,
            maxWidth: 70
        });

    realmNodesGridRequestInfo = new DrRequestInfo({
        endpoint: Endpoint.realmManager_listNodes,
        responseName: 'nodes',
        data: { count: 50, startIndex: 0, filters: [], systemScope: true },
    });

    realmNodesGridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true
    })

    // aeComponentsGridInfo
    asComponentsGridName = 'AE Components Grid';
    aeComponentsGridAutoID = 'ae-components';
    isaeComponentsGridBusy: boolean = false;
    aeComponentsGridBusyMessage = '';

    aeComponentGridColDef = new Array<GenericColDef>(
        {
            headerName: 'Component Name',
            field: 'name',
            automationId: this.aeComponentsGridAutoID,
            minWidth: 30,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        }, {
        headerName: 'Status',
        field: 'monitorStatus',
        cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
        alternativeFieldNames: { iconClass: 'statusIconClass', iconColor: 'statusIconColor' },
        automationId: this.aeComponentsGridAutoID,
        headerAlignDirection: AlignDirection.center,
        cellAlignDirection: AlignDirection.center,
        width: 70, minWidth: 70, maxWidth: 70
    }, {
        headerName: 'Memory',
        field: 'formattedMemory',
        automationId: this.aeComponentsGridAutoID,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 70, minWidth: 70, maxWidth: 70,
        cellTemplateType: CellTemplateType.basicLabelWithTooltip
    }, {
        headerName: 'Threads',
        field: 'numberThreadsExecuting',
        automationId: this.aeComponentsGridAutoID,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 70, minWidth: 70, maxWidth: 70
    }, {
        headerName: 'Last Poll Date/Time',
        field: 'lastPollTime',
        automationId: this.aeComponentsGridAutoID,
        cellAlignDirection: AlignDirection.right,
        headerAlignDirection: AlignDirection.right,
        width: 145, minWidth: 145,
        cellTemplateType: CellTemplateType.formattedDateOrToday
    });

    // Storage Grid Info
    storageGridName: string = 'Storage Grid';
    storageGridAutoID: string = 'storage';
    isStorageGridBusy: boolean = false;
    storageGridBusyMessage: string = '';

    storageGridColDef = new Array<GenericColDef>(
        {
            headerName: 'Storage Name',
            field: 'name',
            automationId: this.storageGridAutoID,
            minWidth: 30,
            defaultSortAscending: true,
            defaultSortBy: 'name',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        }, {
        headerName: 'Status',
        field: 'monitorStatus',
        automationId: this.storageGridAutoID,
        cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
        cellAlignDirection: AlignDirection.center,
        headerAlignDirection: AlignDirection.center,
        alternativeFieldNames: { iconClass: 'statusIconClass', iconColor: 'statusIconColor' },
        width: 95, minWidth: 95, maxWidth: 95
    }, {
        headerName: 'Total Capacity',
        field: 'storageTotal',
        automationId: this.storageGridAutoID,
        cellAlignDirection: AlignDirection.right,
        headerAlignDirection: AlignDirection.right,
        width: 105, minWidth: 105, maxWidth: 105,
        cellTemplateType: CellTemplateType.basicLabelWithTooltip
    }, {
        field: 'currentUsage',
        headerName: 'Used',
        automationId: this.storageGridAutoID,
        cellAlignDirection: AlignDirection.right,
        headerAlignDirection: AlignDirection.right,
        width: 55, minWidth: 55, maxWidth: 55,
        cellTemplateType: CellTemplateType.basicLabelWithTooltip
    });

    storageGridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true,
    });

    // Connectors Grid Info
    connectorsGridName: string = 'Connectors Grid';
    connectorsGridAutoID: string = 'connectors';
    isConnectorsGridBusy: boolean = false;
    connectorsGridBusyMessage: string = '';
    connectorsGridRowSelection = GridRowSelectionType.single;

    connectorsGridColDef = new Array<GenericColDef>({
        headerName: 'Connector Name',
        field: 'name',
        automationId: this.connectorsGridAutoID,
        filterType: FilterTemplateType.freeform,
        minWidth: 30,
        defaultSortAscending: true,
        defaultSortBy: 'name',
        cellTemplateType: CellTemplateType.basicLabelWithTooltip,

    }, {
        headerName: 'Status',
        field: 'monitorStatus',
        automationId: this.connectorsGridAutoID,
        cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
        filterType: FilterTemplateType.multipleIconsButtonSelection,
        width: 68, minWidth: 68, maxWidth: 68,
        alternativeFieldNames: { iconClass: 'statusIconClass', iconColor: 'statusIconColor' },
        cellAlignDirection: AlignDirection.center,
        headerAlignDirection: AlignDirection.center,
        applyFilterOnLoad: false
    }, {
        headerName: 'Organization Name',
        field: 'orgName',
        automationId: this.connectorsGridAutoID,
        filterType: FilterTemplateType.comboBox,
        minWidth: 30,
        cellAlignDirection: AlignDirection.left,
        headerAlignDirection: AlignDirection.left,
        cellTemplateType: CellTemplateType.basicLabelWithTooltip
    }
    );

    connectorGridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true,
        onFilterApply: (filterValue: string, filterType: string, columnName: string) => this.onConnectorsGridFilterApply(filterValue, filterType, columnName)
    });

    // Data Areas Grid info
    dataAreaGridName: string = 'Data Areas Grid';
    dataAreasGridAutoId: string = 'data-areas';
    isDataAreasGridBusy: boolean = false;
    isDataAreasGridBusyMessage: string = '';

    dataAreasColDef = new Array<GenericColDef>({
        headerName: 'Data Area name',
        field: 'areaName',
        automationId: this.dataAreasGridAutoId,
        filterType: FilterTemplateType.freeform,
        cellTemplateType: CellTemplateType.basicLabelWithTooltip,
        minWidth: 30,
        defaultSortBy: 'areaName',
    }, {
        headerName: 'Index Manager',
        field: 'indexManagerStatus',
        cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
        automationId: this.dataAreasGridAutoId,
        alternativeFieldNames: { iconClass: 'indexManagerStatusIconClass', iconColor: 'indexManagerStatusIconColor', tooltip: 'indexManagerStatusTooltip' },
        cellAlignDirection: AlignDirection.center,
        headerAlignDirection: AlignDirection.center,
        width: 116, minWidth: 116, maxWidth: 116
    }, {
        headerName: 'Collection Manager',
        field: 'collectionManagerStatus',
        cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
        alternativeFieldNames: { iconClass: 'collectionManagerStatusIconClass', iconColor: 'collectionManagerStatusIconColor', tooltip: 'collectionManagerStatusTooltip' },
        automationId: this.dataAreasGridAutoId,
        cellAlignDirection: AlignDirection.center,
        headerAlignDirection: AlignDirection.center,
        width: 144, minWidth: 144, maxWidth: 144
    }, {
        headerName: 'Export Manager',
        field: 'exportManagerStatus',
        cellTemplateType: CellTemplateType.iconOnlyFieldWithTooltip,
        alternativeFieldNames: { iconClass: 'exportManagerStatusIconClass', iconColor: 'exportManagerStatusIconColor', tooltip: 'exportManagerStatusTooltip' },
        automationId: this.dataAreasGridAutoId,
        cellAlignDirection: AlignDirection.center,
        headerAlignDirection: AlignDirection.center,
        width: 120, minWidth: 120, maxWidth: 120
    });

    dataAreasGridContext = new GridContext({
        onFilterApply: (queryStr: string) => this.onDataAreasGridFilterApply(queryStr),
        sortBy: 'areaName',
        sortAscending: true
    });

    ngOnInit(): void {
        this.rightBottomPanelText = 'Select a Realm Node to see its Connector and Data Area Status';
    }

    constructor(private httpService: HttpService, private commonService: CommonService, private restService: RestService) {
        this.restService.post(Endpoint.realmManager_listOrganizations, { count: 0, startIndex: 0, filters: [], systemScope: true }).then(response => {
            const orgNameColDef = this.connectorsGridColDef.find(f => f.field === 'orgName');
            if (response?.organizations && orgNameColDef) {
                orgNameColDef.filterItems = response.organizations.map(q => q.name).sort();
                if (this.connectorsGrid) {
                    if (this.connectorsGrid?.gridApi) {
                        this.connectorsGrid.gridApi.setGridOption('columnDefs', this.connectorsGridColDef);
                    }
                    this.connectorsGrid.refreshColumns();
                }
            }
        });
    }

    realmNodesResponseArrayCallback = (response: any): MonitoringNode => {
        response.istoggleOn = true;
        return new MonitoringNode(response);
    }

    onRealmNodesGridRowClickedCallback = (event) => {
        this.realmNodesSelectedRow = event.api.getSelectedRows()[0];
        this.selectedRealmNodeName = this.realmNodesSelectedRow.name;
        this.showAEComponentsandStorageGrids = true;
        this.initialConnectorStatusList = [];

        this.httpService.post(Endpoint.realmManager_getNodeStatus, { handle: this.realmNodesSelectedRow.handle }).then((response) => {
            if (response && response.componentStatusList && response.storageStatusList && response.connectorStatusList) {
                let storageStatusList = new Array<StorageNode>();
                let componentStatusList = new Array<AEComponentNode>();
                response.storageStatusList.forEach(element => {
                    const totalCapacity = parseInt(element.kbAvailable) + parseInt(element.kbUsed);
                    let getIconInfo = this.getIconInfo(element.monitorStatus);
                    element.statusIconClass = getIconInfo.statusIconClass ? getIconInfo.statusIconClass : '';
                    element.statusIconColor = getIconInfo.statusIconColor ? getIconInfo.statusIconColor : '';
                    element.tooltip = getIconInfo.tooltip ? getIconInfo.tooltip : '';
                    element.storageTotal = this.commonService.formatSizeUnits(totalCapacity > 0 ? totalCapacity * 1024 : 0);
                    element.currentUsage = totalCapacity > 0 ? (parseInt(element.kbUsed) / totalCapacity * 100).toFixed(0) + ' %' : '';
                    storageStatusList.push(element);
                });
                response.componentStatusList.forEach(element => {
                    let getIconInfo = this.getIconInfo(element.monitorStatus);
                    const totalCapacity = parseInt(element.memory);
                    element.statusIconClass = getIconInfo.statusIconClass ? getIconInfo.statusIconClass : '';
                    element.statusIconColor = getIconInfo.statusIconColor ? getIconInfo.statusIconColor : '';
                    element.tooltip = getIconInfo.tooltip ? getIconInfo.tooltip : '';
                    element.formattedMemory = this.commonService.formatSizeUnits(totalCapacity > 0 ? totalCapacity : 0);
                    componentStatusList.push(element);
                });
                response.connectorStatusList.forEach(element => {
                    let getIconInfo = this.getIconInfo(element.monitorStatus);
                    element.statusIconClass = getIconInfo.statusIconClass ? getIconInfo.statusIconClass : '';
                    element.statusIconColor = getIconInfo.statusIconColor ? getIconInfo.statusIconColor : '';
                    element.tooltip = getIconInfo.tooltip ? getIconInfo.tooltip : '';
                    this.initialConnectorStatusList.push(element);
                });

                this.aeComponentsGrid.setData(componentStatusList);
                const statusColDef = this.connectorsGridColDef.find(f => f.field === 'monitorStatus');
                this.connectorsGrid.setData(this.initialConnectorStatusList);
                if (statusColDef.applyFilterOnLoad && !this.gridsUpdated) {
                    this.gridsUpdated = false;
                    this.onConnectorsGridFilterApply('PARTIAL_FAILURE WARNING FAILURE CRITICAL UNKNOWN', FilterTemplateType.multipleIconsButtonSelection, 'Status');
                }
                this.storageGrid.setData(storageStatusList);
                this.rightBottomPanelText = 'Select a Connector to see its Data Areas';
            }
        });
    }

    onConnectorsGridRowClickedCallback = (event) => {
        this.connectorsGridSelectedRow = event.api.getSelectedRows()[0];
        this.selectedConnectorGridName = this.connectorsGridSelectedRow.name;
        this.initialDataAreaStatusList = [];

        this.httpService.post(Endpoint.realmManager_listConnectorDataAreaStatus, {
            handle: this.connectorsGridSelectedRow.handle,
            realmNodeId: this.realmNodesSelectedRow.handle,
            systemScope: true
        }).then((response) => {
            response.dataAreaStatusList.forEach(element => {
                element.indexManagerStatus = element.components.find(el => el.name === 'Index Manager').monitorStatus;
                element.collectionManagerStatus = element.components.find(el => el.name === 'Collection Manager').monitorStatus;
                element.exportManagerStatus = element.components.find(el => el.name === 'Export Manager').monitorStatus;

                const indexManagerIconInfo = this.getIconInfo(element.indexManagerStatus);
                const collectionManagerIconInfo = this.getIconInfo(element.collectionManagerStatus);
                const exportManagerIconInfo = this.getIconInfo(element.exportManagerStatus);

                element.indexManagerStatusIconClass = indexManagerIconInfo.statusIconClass ? indexManagerIconInfo.statusIconClass : '';
                element.indexManagerStatusIconColor = indexManagerIconInfo.statusIconColor ? indexManagerIconInfo.statusIconColor : '';
                element.indexManagerStatusTooltip = indexManagerIconInfo.tooltip ? indexManagerIconInfo.tooltip : '';
                element.collectionManagerStatusIconClass = collectionManagerIconInfo.statusIconClass ? collectionManagerIconInfo.statusIconClass : '';
                element.collectionManagerStatusIconColor = collectionManagerIconInfo.statusIconColor ? collectionManagerIconInfo.statusIconColor : '';
                element.collectionManagerStatusTooltip = collectionManagerIconInfo.tooltip ? collectionManagerIconInfo.tooltip : '';
                element.exportManagerStatusIconClass = exportManagerIconInfo.statusIconClass ? exportManagerIconInfo.statusIconClass : '';
                element.exportManagerStatusIconColor = exportManagerIconInfo.statusIconColor ? exportManagerIconInfo.statusIconColor : '';
                element.exportManagerStatusTooltip = exportManagerIconInfo.tooltip ? exportManagerIconInfo.tooltip : '';
                this.initialDataAreaStatusList.push(element);
            });

            this.dataAreasGrid.setData(this.initialDataAreaStatusList);
        });
    }

    getIconInfo(monitorStatus: string): any {
        let statusIconClass: string = '';
        let statusIconColor: string = '';
        let tooltip: string = '';
        switch (monitorStatus) {
            case MonitorStatus.AVAILABLE: {
                statusIconClass = 'fa-check';
                statusIconColor = 'color-green-04';
                tooltip = MonitorStatus.AVAILABLE;
                break;
            }
            case MonitorStatus.PARTIAL_FAILURE:
            case MonitorStatus.WARNING: {
                statusIconClass = 'fa-exclamation-triangle';
                statusIconColor = 'color-tan-07';
                tooltip = MonitorStatus.WARNING;
                break;
            }
            case MonitorStatus.FAILURE:
            case MonitorStatus.CRITICAL: {
                statusIconClass = 'fa-exclamation-circle';
                statusIconColor = 'color-red-05';
                tooltip = 'Critical / Failure';
                break;
            }
            case MonitorStatus.NOT_LICENSED: {
                statusIconClass = 'fa-certificate';
                statusIconColor = 'color-purple-03';
                tooltip = MonitorStatus.NOT_LICENSED;
                break;
            }
            case MonitorStatus.UNKNOWN: {
                statusIconClass = 'fa-question-circle';
                statusIconColor = 'color-grey-08';
                tooltip = MonitorStatus.UNKNOWN;
                break;
            }
            case MonitorStatus.DEACTIVATED: {
                statusIconClass = 'fa-times-circle';
                statusIconColor = 'color-grey-08';
                tooltip = MonitorStatus.DEACTIVATED;
                break;
            }
        }

        return { statusIconClass: statusIconClass, statusIconColor: statusIconColor, tooltip: tooltip };
    }

    onDataAreasGridFilterApply(queryStr: string) {
        this.isDataAreasGridBusy = true;
        this.isDataAreasGridBusyMessage = 'Loading Data Areas grid';
        const filteredDataAreas = _.filter(this.initialDataAreaStatusList, (dataArea) => dataArea.areaName ? dataArea.areaName.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
        this.dataAreasGrid.setData(filteredDataAreas);
        this.isDataAreasGridBusy = false;
    }

    onConnectorsGridFilterApply(filterValue: string, filterType: string, columnName: string) {
        // retain the filter state when realm nodes selection changes
        const statusColDef = this.connectorsGridColDef.find(f => f.field === 'monitorStatus');
        statusColDef.applyFilterOnLoad = filterType === 'clear' && columnName === 'Status' ? false : true;
        this.isConnectorsGridBusy = true;
        this.connectorsGridBusyMessage = 'Loading Connectors grid';
        this.connectorNameColumnFilterValue = columnName === 'Connector Name' ? filterValue : this.connectorNameColumnFilterValue;
        this.statusColumnFilterValue = columnName === 'Status' && filterType !== 'clear' ? filterValue : columnName === 'Status' && filterType === 'clear' ? filterValue + ' AVAILABLE' : this.statusColumnFilterValue;
        this.orgNameColumnFilterValue = columnName === 'Organization Name' ? filterValue : this.orgNameColumnFilterValue;

        let statusArray = [];
        statusArray = filterType === FilterTemplateType.multipleIconsButtonSelection ? filterValue.split(' ') : (columnName === 'Status' && filterType === 'clear') ? this.statusColumnFilterValue.split(' ') : this.statusColumnFilterValue.split(' ');
        const filteredConnectors = _.filter(this.initialConnectorStatusList, (connectorStatus) => {
            return (connectorStatus.name.toLowerCase().indexOf(this.connectorNameColumnFilterValue.toLowerCase()) !== -1) &&
                (connectorStatus.orgName.toLowerCase().indexOf(this.orgNameColumnFilterValue.toLowerCase()) !== -1) &&
                (statusArray.some(status => connectorStatus.monitorStatus.toLowerCase().includes(status.toLowerCase())));
        });
        this.connectorsGrid.setData(filteredConnectors);
        this.isConnectorsGridBusy = false;
    }

    updateGrids() {
        this.gridsUpdated = true;
        this.realmNodesGrid.loadData();
        this.showAEComponentsandStorageGrids = false;
        this.connectorsGridSelectedRow = null;
        this.rightBottomPanelText = 'Select a Realm Node to see its Connector and Data Area Status';

        // clear filters 'Connector Name', 'Status' and 'Organization Name' from the Connectors grid
        this.onConnectorsGridFilterApply('', FilterTemplateType.clear, 'Connector Name');
        this.onConnectorsGridFilterApply('', 'clear', 'Status');
        this.onConnectorsGridFilterApply('', FilterTemplateType.clear, 'Organization Name');
        this.connectorsGrid.gridApi.refreshHeader();
    }

    openHelp = (): void => this.commonService.openHelp(HelpIndex.sys_monitoring_dlg);
}
